import { indicatorAutoLabel } from "@strat/format";
import type { IndicatorDef } from "@strat/format";

import { drawsOnChart, indicatorColor } from "../theme/chartTheme";
import type { useChartView } from "./chartView";

/**
 * The filter bar over a bot's price chart — the same one in the browser and
 * the desktop app: which signals to draw (entries, exits, the line between
 * them), which side's trades, and which of the chart's
 * other layers (position, stop, volume, each indicator).
 */

export function ChartFilters({
  state,
  indicators,
  position,
  hasStop,
}: {
  state: ReturnType<typeof useChartView>;
  indicators: IndicatorDef[];
  /** The open position, when there is one: its toggles only appear then. */
  position?: { direction: string } | null;
  hasStop?: boolean;
}) {
  const { view, update, hiddenIds, toggleIndicator, bothSides } = state;
  return (
    <div className="chart-filters" role="group" aria-label="What the chart shows">
      <div className="filter-group">
        <span className="filter-group__label">Signals</span>
        <LayerToggle
          on={view.entries}
          onClick={() => update({ entries: !view.entries })}
          swatch="▲"
          tone="up"
        >
          Entries
        </LayerToggle>
        <LayerToggle on={view.exits} onClick={() => update({ exits: !view.exits })} swatch="▼" tone="down">
          Exits
        </LayerToggle>
        <LayerToggle
          on={view.tradeLines}
          onClick={() => update({ tradeLines: !view.tradeLines })}
          swatch="┈"
          title="A dotted line from each entry to its exit"
        >
          Trade lines
        </LayerToggle>
      </div>
      {bothSides && (
        <div className="segmented" role="group" aria-label="Which side">
          {(["all", "long", "short"] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={view.side === value ? "is-active" : undefined}
              aria-pressed={view.side === value}
              onClick={() => update({ side: value })}
            >
              {value === "all" ? "Both sides" : value === "long" ? "Long" : "Short"}
            </button>
          ))}
        </div>
      )}
      <div className="filter-group">
        <span className="filter-group__label">Show</span>
        {position && (
          <>
            <LayerToggle
              on={view.position}
              onClick={() => update({ position: !view.position })}
              swatch="╌"
              tone={position.direction === "short" ? "down" : "up"}
            >
              Position
            </LayerToggle>
            {hasStop && (
              <LayerToggle
                on={view.stop}
                onClick={() => update({ stop: !view.stop })}
                swatch="┈"
                tone="down"
                title="Drawn while the stop is inside the price range on screen"
              >
                Stop
              </LayerToggle>
            )}
          </>
        )}
        <LayerToggle on={view.volume} onClick={() => update({ volume: !view.volume })} swatch="▮">
          Volume
        </LayerToggle>
        {indicators
          .filter((d) => drawsOnChart(d.kind))
          .map((d) => (
            <LayerToggle
              key={d.id}
              on={!hiddenIds.has(d.id)}
              onClick={() => toggleIndicator(d.id)}
              swatch="━"
              color={indicatorColor(d)}
            >
              {d.label?.trim() || indicatorAutoLabel(d, indicators)}
            </LayerToggle>
          ))}
      </div>
    </div>
  );
}

/** One on/off switch in the filter bar: a pill that dims when its layer is hidden. */
function LayerToggle({
  on,
  onClick,
  swatch,
  tone,
  color,
  title,
  children,
}: {
  on: boolean;
  onClick: () => void;
  swatch: string;
  tone?: "up" | "down";
  color?: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="layer-toggle" aria-pressed={on} onClick={onClick} title={title}>
      <span
        className={`layer-toggle__swatch${tone ? ` ${tone}` : ""}`}
        style={color ? { color } : undefined}
        aria-hidden="true"
      >
        {swatch}
      </span>
      {children}
    </button>
  );
}
